import { useState, useEffect, useCallback } from 'react';
import API from '../API';
// Helpers
import { isPersistedState, persistState } from '../helpers';

export const useMovieFetch = movieId => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchMovie = useCallback(
    async isCancelled => {
      try {
        setLoading(true);
        setError(false);

        const [movie, credits] = await Promise.all([
          API.fetchMovie(movieId),
          API.fetchCredits(movieId)
        ]);
        if (!movie || !movie.id || !credits || !credits.cast)
          throw new Error('Movie not found');
        if (isCancelled()) return;

        // Get directors only
        const directors = credits.crew.filter(
          member => member.job === 'Director'
        );
        const writers = credits.crew
          .filter(member => ['Screenplay', 'Writer', 'Story'].includes(member.job))
          .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i)
          .slice(0, 3);

        const next = { ...movie, actors: credits.cast, directors, writers };
        setState(next);
        persistState(`movie-${movieId}`, next);
      } catch (error) {
        if (!isCancelled()) setError(true);
      }
      if (!isCancelled()) setLoading(false);
    },
    [movieId]
  );

  useEffect(() => {
    let cancelled = false;
    const sessionState = isPersistedState(`movie-${movieId}`);

    if (sessionState && sessionState.id) {
      setState(sessionState);
      setError(false);
      setLoading(false);
      return;
    }

    setState(null);
    fetchMovie(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [movieId, fetchMovie]);

  const retry = () => fetchMovie(() => false);

  return { state, loading, error, retry };
};
